import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-routing-machine/dist/leaflet-routing-machine.css';
import 'leaflet-routing-machine';
import { toast } from 'react-toastify';
import { updateShipmentSequences, removeShipmentFromRoute, resetShipmentStatus, addCommentToShipment } from '../utils/api';
import { getDriverColor, DEFAULT_MAP_CENTER } from '../utils/routeUtils';
import { setupDefaultIcon, getRouteStopIcon } from './map/MapIcons';
import MapBoundsFitter from './map/MapBoundsFitter';
import MapViewPreserver from './map/MapViewPreserver';
import RouteDisplay from './map/RouteDisplay';
import { useNavigate } from 'react-router-dom';
import DriverRouteDndList from './DriverRouteDndList';

// Setup default icon
setupDefaultIcon();

/**
 * OptimalRoutesMap Component
 * --------------------------
 * Specialized map component for displaying optimal delivery routes.
 * Shows numbered stops and color-codes by driver.
 *
 * Props:
 *  - routes (Array): Array of route stop objects with coordinates
 *  - deliveryDate (String): Current delivery date (YYYY-MM-DD)
 *  - iterationId (String): Current iteration ID
 *  - loadOptimalRoutes (Function): Function to reload route data
 *  - selectedShipment (Object): Selected shipment from search
 */
function OptimalRoutesMap({ routes = [], deliveryDate, iterationId, loadOptimalRoutes, selectedShipment, setDriverDistances }) {
  const [validPoints, setValidPoints] = useState([]);
  const [driverRoutes, setDriverRoutes] = useState({});
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [isRoutesPanelOpen, setIsRoutesPanelOpen] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [groupedShipments, setGroupedShipments] = useState({});
  const [messageText, setMessageText] = useState('');
  const navigate = useNavigate();

  // Function to calculate distance between two points using OSRM
  const calculateRouteDistance = async (startLat, startLng, endLat, endLng) => {
    try {
      const NODE_SERVER_URL = process.env.REACT_APP_NODE_SERVER_URL || '';
      const response = await fetch(
        `${NODE_SERVER_URL}/api/routes/osrm/route/v1/driving/${startLng},${startLat};${endLng},${endLat}?overview=false`
      );
      
      if (!response.ok) {
        throw new Error('OSRM routing failed');
      }
      
      const data = await response.json();
      
      if (data.code !== 'Ok') {
        throw new Error(`OSRM error: ${data.code}`);
      }
      
      // Return distance in kilometers
      return Math.round(data.routes[0].distance / 1000);
    } catch (error) {
      console.error('Error calculating route distance:', error);
      return 0;
    }
  };

  // Calculate total distance for each driver's route
  const calculateDriverDistances = async () => {
    const driverDistances = {};
    
    for (const driverId in driverRoutes) {
      const stops = driverRoutes[driverId];
      
      if (!stops || stops.length < 2) {
        driverDistances[driverId] = 0;
        continue;
      }
      
      let totalDistance = 0;
      
      // Sort stops by sequence
      const sortedStops = [...stops].sort((a, b) => a.sequence - b.sequence);
      
      // Calculate distance between consecutive stops
      for (let i = 0; i < sortedStops.length - 1; i++) {
        const currentStop = sortedStops[i];
        const nextStop = sortedStops[i + 1];
        
        // Get coordinates for current stop

        // lat: latitude,
        // lng: longitude,

        let currentLat = currentStop.lat;
        let currentLng = currentStop.lng;
        
        // Get coordinates for next stop
        let nextLat = nextStop.lat;
        let nextLng = nextStop.lng;
        
        // Skip if coordinates are invalid
        if (!currentLat || !currentLng || !nextLat || !nextLng) {
          continue;
        }
        
        // Calculate distance between stops
        const distance = await calculateRouteDistance(currentLat, currentLng, nextLat, nextLng);
        totalDistance += distance;
      }
      
      driverDistances[driverId] = parseFloat(totalDistance.toFixed(0));
    }
    
    return driverDistances;
  };

  useEffect(() => {
    async function run() {
      const driverDistances = await calculateDriverDistances();
      console.log('Driver distances:', driverDistances);
      setDriverDistances(driverDistances);
    }
    run();
  }, [driverRoutes, setDriverDistances]);

  // Process route data
  useEffect(() => {
      if (!routes || !Array.isArray(routes)) {
        setValidPoints([]);
        setDriverRoutes({});
        setGroupedShipments({});
      return;
    }

    // Clear the valid points array first
    const validPointsArray = [];
    const points = routes
      .map(point => {
        let latitude = null;
        let longitude = null;
        let address = '';

        // Check if this is a pickup (based on is_pickup field from backend)
        const isPickup = point?.delivery_partner?.toLowerCase() === 'np_pickup'
        || point?.delivery_type?.toLowerCase() === 'unknown'
        || ( !point?.delivery_partner && point?.delivery_type?.toLowerCase() === 'pickup');

        if (isPickup) {
          latitude = parseFloat(point?.sender?.address?.latitude);
          longitude = parseFloat(point?.sender?.address?.longitude);
          address = point?.sender?.address?.string;
        } else {
          latitude = parseFloat(point?.recipient?.address?.latitude);
          longitude = parseFloat(point?.recipient?.address?.longitude);
          address = point?.recipient?.address?.string;
        }

        // Create a point object that matches MarkShipmentsMap format
        const formattedPoint = {
          // Map to the exact field names used in MarkShipmentsMap
          'Delivery Name': point.shipment_name,
          'Formatted Address': address,
          'Delivery Type': point.delivery_type,
          'Name': isPickup ? point?.sender?.name : point?.recipient?.name,
          'Phone Number': isPickup ? point?.sender?.phone : point?.recipient?.phone,

          // Keep routing-specific fields
          id: point.shipment_name,
          driverNumber: point.driver_id || 1,
          driverName: point.name || `Driver ${point.driver_id || 1}`,
          displayOrder: point.sequence || '',
          sequence: parseInt(point.sequence, 10) || 0,

          // Standard location fields
          lat: latitude,
          lng: longitude,

          // Physical properties
          weight: point.weight || 0,
          volume: point.volume || 0,

          // Maintain compatibility with RouteDisplay
          point: {
            id: point.shipment_name,
            driverName: point.name || `Driver ${point.driver_id || 1}`,
            latitude,
            longitude,
            address
          },

          // Keep original fields for reference
          ...point
        };

        // Add to validPointsArray if coordinates are valid
        if (!isNaN(latitude) && !isNaN(longitude)) {
          validPointsArray.push(formattedPoint);
        }

        return formattedPoint;
      });

    console.log('Total route points:', points.length);
    console.log('Valid points for map:', validPointsArray.length);

    // Group points by driver
    const byDriver = {};
    const driverInfo = {};

    // First pass: group by driver and collect driver info
    points.forEach(point => {
      const driver = point.driverName;

      if (!byDriver[driver]) {
        byDriver[driver] = [];

        // Store driver info (for potential home points)
        if (!driverInfo[driver] && point.driver_id) {
          // Find the driver object in the routes data
          const driverData = routes.find(r => r.id === point.driver_id);
          if (driverData) {
            driverInfo[driver] = {
              id: driverData.id,
              name: driverData.name,
              latitude: driverData.latitude,
              longitude: driverData.longitude,
              address: driverData.home_address
            };
          }
        }
      }
      byDriver[driver].push(point);
    });

    // Second pass: check for sequence 0 points and add home points if needed
    Object.keys(byDriver).forEach(driver => {
      let routePoints = byDriver[driver].sort((a, b) => a.sequence - b.sequence);

      // Check if there's a sequence 0 point
      const hasSequenceZero = routePoints.some(p => p.sequence === 0);

      // If no sequence 0 point and we have driver info, add a home point
      if (!hasSequenceZero && driverInfo[driver]) {
        const driverData = driverInfo[driver];

        // Create a home point for this driver
        const homePoint = {
          'Delivery Name': 'HOME',
          'Formatted Address': driverData.address || 'Driver Home',
          'Delivery Type': 'Home',
          'Name': driverData.name,
          'Phone Number': '',

          id: `HOME-${driverData.id}`,
          driverNumber: driverData.id,
          driverName: driver,
          displayOrder: '0',
          sequence: 0,

          // Use driver's home coordinates
          lat: parseFloat(driverData.latitude),
          lng: parseFloat(driverData.longitude),

          // Support RouteDisplay
          point: {
            id: `HOME-${driverData.id}`,
            driverName: driver,
            latitude: parseFloat(driverData.latitude),
            longitude: parseFloat(driverData.longitude),
            address: driverData.address || 'Driver Home'
          },

          // Add a flag to identify this as a home point
          isHome: true
        };

        // Function to get the latest sequence number in the route
        const getLatestSequence = (points) => {
          if (!points || points.length === 0) return 0;
          return Math.max(...points.map(point => point.sequence || 0));
        };

        // Ensure the home point has the highest sequence number for the return trip
        const latestSequence = getLatestSequence(routePoints);

        // Only add if coordinates are valid
        if (!isNaN(homePoint.lat) && !isNaN(homePoint.lng)) {
          routePoints.unshift(homePoint);
          routePoints.push({...homePoint, sequence: latestSequence + 1});
          validPointsArray.push(homePoint);
        }
      }

      // Re-sort after potentially adding a home point
      routePoints = routePoints.sort((a, b) => a.sequence - b.sequence);

      // Update the driver routes
      byDriver[driver] = routePoints;
    });

    // Group shipments by address coordinates
    const grouped = {};
    validPointsArray.forEach(point => {
      // Skip points with invalid coordinates
      if (isNaN(point.lat) || isNaN(point.lng)) return;

      // Create a key based on coordinates (rounded to 6 decimal places for precision)
      const key = `${point.lat.toFixed(6)},${point.lng.toFixed(6)}`;

      if (!grouped[key]) {
        grouped[key] = {
          lat: point.lat,
          lng: point.lng,
          address: point['Formatted Address'] || '',
          shipments: [],
          locationKey: key // Store the key for reference
        };
      }

      grouped[key].shipments.push(point);
    });

    // Now we need to reorganize the driver routes to group by address
    const groupedByDriverAndAddress = {};

    Object.keys(byDriver).forEach(driver => {
      if (!groupedByDriverAndAddress[driver]) {
        groupedByDriverAndAddress[driver] = [];
      }

      // Create a map to track what's been added
      const addedLocationKeys = new Set();

      // Process each point in the original driver route
      byDriver[driver].forEach(point => {
        // Skip points with invalid coordinates
        if (isNaN(point.lat) || isNaN(point.lng)) return;

        // Home points are always handled individually
        if (point.isHome) {
          groupedByDriverAndAddress[driver].push({
            ...point,
            isGroup: false,
            locationKey: null,
            groupedShipments: [point]
          });
          return;
        }

        // For regular points, check if they should be grouped
        const locationKey = `${point.lat.toFixed(6)},${point.lng.toFixed(6)}`;

        // If this location has already been added, skip
        if (addedLocationKeys.has(locationKey)) return;

        // Mark this location as added
        addedLocationKeys.add(locationKey);

        // Get all shipments at this location
        const shipmentsAtLocation = grouped[locationKey]?.shipments || [];

        // If there's more than one shipment or we're explicitly grouping singles
        if (shipmentsAtLocation.length >= 1) {
          // Create a group object that represents all shipments at this location
          // Use the one with the lowest sequence as the primary
          const sortedShipments = [...shipmentsAtLocation].sort((a, b) =>
            (a.sequence || 999) - (b.sequence || 999)
          );

          const primaryShipment = sortedShipments[0];

          // Create a grouped entry
          const groupEntry = {
            ...primaryShipment,
            isGroup: true,
            locationKey,
            groupedShipments: sortedShipments,
            shipmentCount: sortedShipments.length,
            // Use the primary shipment's details for display
            id: locationKey, // Use locationKey as id to ensure uniqueness
            displayName: primaryShipment['Delivery Name'] === 'DEPO'
              ? 'DEPO'
              : (sortedShipments.length > 1
                ? `${sortedShipments.length} shipments`
                : primaryShipment['Delivery Name'])
          };

          groupedByDriverAndAddress[driver].push(groupEntry);
        }
      });

      // Sort the grouped driver points by sequence
      groupedByDriverAndAddress[driver].sort((a, b) => a.sequence - b.sequence);
    });

    console.log('Total points:', validPointsArray.length);
    console.log('Grouped addresses:', Object.keys(grouped).length);
    setGroupedShipments(grouped);

    console.log('Route points by driver:', Object.keys(byDriver).map(d => ({
      driver: d,
      count: byDriver[d].length
    })));

    // Set the first driver as selected by default
    if (!selectedDriver && Object.keys(groupedByDriverAndAddress).length > 0) {
      setSelectedDriver(Object.keys(groupedByDriverAndAddress)[0]);
    }

    // Store both the original and grouped driver routes
    setDriverRoutes(groupedByDriverAndAddress);
    setValidPoints(validPointsArray);
  }, [routes, selectedDriver]);

  // First, add this simple debug function to see what's happening
  const debugPoints = () => {
    if (!selectedDriver || !driverRoutes[selectedDriver]) return;
    console.log("Current points:", driverRoutes[selectedDriver].map(p =>
      `ID: ${p.id}, Name: ${p.displayName || p['Delivery Name']}, Seq: ${p.sequence}, IsGroup: ${p.isGroup}`
    ));
  };

  // Helper function to update all states after sequence changes
  const updateStateAfterSequenceChange = (newDriverRoutes, newValidPoints, swappedPoints) => {
    // Create an updated grouped shipments object based on the NEW valid points
    // instead of the current groupedShipments
    const updatedGroupedShipments = {};

    // Group the new valid points by location
    newValidPoints.forEach(point => {
      // Skip points with invalid coordinates or home points (handled separately)
      if (isNaN(point.lat) || isNaN(point.lng)) return;

      // Create a key based on coordinates (rounded to 6 decimal places for precision)
      const key = `${point.lat.toFixed(6)},${point.lng.toFixed(6)}`;

      if (!updatedGroupedShipments[key]) {
        updatedGroupedShipments[key] = {
          lat: point.lat,
          lng: point.lng,
          address: point['Formatted Address'] || '',
          shipments: [],
          locationKey: key
        };
      }

      // Only add shipments for the current driver or existing shipments for other drivers
      const shouldAdd =
        (point.driverName === selectedDriver) || // Current driver's points
        (groupedShipments[key]?.shipments.some(s => s['Delivery Name'] === point['Delivery Name'])); // Or existing points

      if (shouldAdd) {
        updatedGroupedShipments[key].shipments.push(point);
      }
    });

    // First update the driver routes and valid points
    setDriverRoutes(newDriverRoutes);
    setValidPoints(newValidPoints);

    // Then update the grouped shipments with our newly constructed object
    setGroupedShipments(updatedGroupedShipments);

    // Mark that we have unsaved changes
    setHasUnsavedChanges(true);
  };

  // Modify the moveUp function to handle grouped shipments
  const moveUp = (pointId, sequence) => {
    console.log("Trying to move point up, ID:", pointId, "sequence:", sequence);

    if (!selectedDriver) return;

    // Get the current points for this driver
    const driverPoints = [...driverRoutes[selectedDriver]];
    debugPoints();

    // Find the index of the point or group
    const index = driverPoints.findIndex(p => p.id === pointId && p.sequence === sequence);
    console.log(`Point found at index ${index}`);

    // Can't move if it's the first point or not found
    if (index <= 0) {
      console.log("Can't move up: first point or not found");
      return;
    }

    // Skip if it's a HOME point or previous point is HOME
    if (driverPoints[index].isHome) {
      console.log("Can't move: HOME point");
      return;
    }

    if (driverPoints[index-1].isHome) {
      console.log("Can't move past HOME point");
      return;
    }

    // Swap with the point above it
    const temp = { ...driverPoints[index] };
    driverPoints[index] = { ...driverPoints[index-1] };
    driverPoints[index-1] = temp;

    // Now we need to adjust sequences for all points
    // Be careful to maintain the same sequence for all points in the same group
    // and ensure no overlapping sequences between different groups

    // First, create a map from index to sequence
    // This map is from positions (indexes) to sequence values
    const resequence = driverPoints.map((point, idx) => {
      if (point.isHome) return 0; // Home points always have sequence 0
      return idx; // Other points get sequence equal to their position
    });

    // Next, update the points with their new sequences
    const updatedPoints = driverPoints.map((point, idx) => {
      // Make a deep copy of the point
      const updatedPoint = { ...point };

      // Set the sequence based on the resequence map
      const newSequence = resequence[idx];
      updatedPoint.sequence = newSequence;
      updatedPoint.displayOrder = newSequence.toString();

      // If this is a group, we need to update each shipment in the group
      if (updatedPoint.isGroup && updatedPoint.groupedShipments) {
        // Update all shipments in this group to have the same sequence
        updatedPoint.groupedShipments = updatedPoint.groupedShipments.map(shipment => ({
          ...shipment,
          sequence: newSequence,
          displayOrder: newSequence.toString()
        }));
      }

      return updatedPoint;
    });

    // Update the driver routes with the resequenced points
    const newDriverRoutes = { ...driverRoutes };
    newDriverRoutes[selectedDriver] = updatedPoints;

    // Also update validPoints to keep everything in sync
    // We need to update all shipments that are part of the groups that were moved
    const newValidPoints = [...validPoints];

    // The points/groups that were swapped
    const swappedPoints = [driverPoints[index-1], driverPoints[index]];

    // For each swapped point/group
    swappedPoints.forEach((swappedPoint, swapIdx) => {
      const oldIdx = swapIdx === 0 ? index : index-1;
      const newIdx = swapIdx === 0 ? index-1 : index;
      const newSequence = resequence[newIdx];

      // If this is a group, update all shipments in the group
      if (swappedPoint.isGroup && swappedPoint.groupedShipments) {
        swappedPoint.groupedShipments.forEach(groupShipment => {
          // Find this shipment in validPoints
          const shipmentIdx = newValidPoints.findIndex(vp =>
            vp['Delivery Name'] === groupShipment['Delivery Name'] &&
            vp.driverName === selectedDriver
          );

          if (shipmentIdx !== -1) {
            // Update its sequence
            newValidPoints[shipmentIdx] = {
              ...newValidPoints[shipmentIdx],
              sequence: newSequence,
              displayOrder: newSequence.toString()
            };
          }
        });
      }
      // If this is a regular point, update it directly
      else {
        const pointIdx = newValidPoints.findIndex(vp =>
          vp.id === swappedPoint.id &&
          vp.driverName === selectedDriver
        );

        if (pointIdx !== -1) {
          newValidPoints[pointIdx] = {
            ...newValidPoints[pointIdx],
            sequence: newSequence,
            displayOrder: newSequence.toString()
          };
        }
      }
    });

    updateStateAfterSequenceChange(newDriverRoutes, newValidPoints, swappedPoints);

    console.log(`Moved point with ID ${pointId} and sequence ${sequence} up`);
    debugPoints();
  };

  const moveDown = (pointId, sequence) => {
    console.log("Trying to move point down, ID:", pointId, "sequence:", sequence);

    if (!selectedDriver) return;

    // Get the current points for this driver
    const driverPoints = [...driverRoutes[selectedDriver]];
    debugPoints();

    // Find the index of the point or group
    const index = driverPoints.findIndex(p => p.id === pointId && p.sequence === sequence);
    console.log(`Point found at index ${index}`);

    // Can't move if it's the last point or not found
    if (index === -1 || index >= driverPoints.length - 1) {
      console.log("Can't move down: last point or not found");
      return;
    }

    // Skip if it's a HOME point or next point is HOME
    if (driverPoints[index].isHome) {
      console.log("Can't move: HOME point");
      return;
    }

    if (driverPoints[index+1].isHome) {
      console.log("Can't move past HOME point");
      return;
    }

    // Swap with the point below it
    const temp = { ...driverPoints[index] };
    driverPoints[index] = { ...driverPoints[index+1] };
    driverPoints[index+1] = temp;

    // Now we need to adjust sequences for all points
    // Be careful to maintain the same sequence for all points in the same group
    // and ensure no overlapping sequences between different groups

    // First, create a map from index to sequence
    // This map is from positions (indexes) to sequence values
    const resequence = driverPoints.map((point, idx) => {
      if (point.isHome) return 0; // Home points always have sequence 0
      return idx; // Other points get sequence equal to their position
    });

    // Next, update the points with their new sequences
    const updatedPoints = driverPoints.map((point, idx) => {
      // Make a deep copy of the point
      const updatedPoint = { ...point };

      // Set the sequence based on the resequence map
      const newSequence = resequence[idx];
      updatedPoint.sequence = newSequence;
      updatedPoint.displayOrder = newSequence.toString();

      // If this is a group, we need to update each shipment in the group
      if (updatedPoint.isGroup && updatedPoint.groupedShipments) {
        // Update all shipments in this group to have the same sequence
        updatedPoint.groupedShipments = updatedPoint.groupedShipments.map(shipment => ({
          ...shipment,
          sequence: newSequence,
          displayOrder: newSequence.toString()
        }));
      }

      return updatedPoint;
    });

    // Update the driver routes with the resequenced points
    const newDriverRoutes = { ...driverRoutes };
    newDriverRoutes[selectedDriver] = updatedPoints;

    // Also update validPoints to keep everything in sync
    // We need to update all shipments that are part of the groups that were moved
    const newValidPoints = [...validPoints];

    // The points/groups that were swapped
    // In moveDown, we need to make sure we're using the actual points AFTER the swap
    const swappedPoints = [updatedPoints[index], updatedPoints[index+1]];

    // For each swapped point/group, update all shipments with the new sequence
    swappedPoints.forEach((swappedPoint, swapIdx) => {
      const pointPosition = swapIdx === 0 ? index : index+1;
      const newSequence = resequence[pointPosition];

      // If this is a group, update all shipments in the group
      if (swappedPoint.isGroup && swappedPoint.groupedShipments) {
        swappedPoint.groupedShipments.forEach(groupShipment => {
          // Find this shipment in validPoints
          const shipmentIdx = newValidPoints.findIndex(vp =>
            vp['Delivery Name'] === groupShipment['Delivery Name'] &&
            vp.driverName === selectedDriver
          );

          if (shipmentIdx !== -1) {
            // Update its sequence
            newValidPoints[shipmentIdx] = {
              ...newValidPoints[shipmentIdx],
              sequence: newSequence,
              displayOrder: newSequence.toString()
            };
          }
        });
      }
      // If this is a regular point, update it directly
      else if (!swappedPoint.isHome) {
        const pointIdx = newValidPoints.findIndex(vp =>
          vp.id === swappedPoint.id &&
          vp.driverName === selectedDriver
        );

        if (pointIdx !== -1) {
          newValidPoints[pointIdx] = {
            ...newValidPoints[pointIdx],
            sequence: newSequence,
            displayOrder: newSequence.toString()
          };
        }
      }
    });

    // Use the updated helper function to sync all state
    updateStateAfterSequenceChange(newDriverRoutes, newValidPoints, swappedPoints);

    console.log(`Moved point with ID ${pointId} and sequence ${sequence} down`);
    debugPoints();
  };

  // Handle saving route changes
  const saveRouteChanges = async () => {
    try {
      console.log("Saving route changes...");

      if (!deliveryDate || !iterationId || !selectedDriver) {
        throw new Error('Missing delivery date, iteration ID, or selected driver');
      }

      // Prepare the shipment sequences data
      const shipmentSequences = [];

      // Only get the points for the selected driver
      const driverPoints = driverRoutes[selectedDriver] || [];

      // Add each shipment, handling groups appropriately
      driverPoints.forEach(point => {
        // Skip HOME points
        if (point.isHome) return;

        // If this is a group, add each shipment in the group with the same sequence
        if (point.isGroup && point.groupedShipments) {
          point.groupedShipments.forEach(shipment => {
            // Skip HOME points within groups too (though this shouldn't happen)
            if (shipment.isHome) return;

            shipmentSequences.push({
              shipment_name: shipment['Delivery Name'],
              sequence: point.sequence // Use the group's sequence for all shipments in the group
            });
          });
        }
        // If it's a regular point, add it directly
        else {
          shipmentSequences.push({
            shipment_name: point['Delivery Name'],
            sequence: point.sequence
          });
        }
      });

      if (shipmentSequences.length === 0) {
        toast.info('No shipments to update for this driver');
        return;
      }

      console.log("Saving sequences:", shipmentSequences);

      // Send the update to the backend
      const result = await updateShipmentSequences(
        deliveryDate,
        iterationId,
        shipmentSequences
      );

      if (!result.success) {
        throw new Error(result.error || 'Failed to save route changes');
      }

      // After successful save, reset the hasUnsavedChanges flag
      setHasUnsavedChanges(false);

      // Show success message
      toast.success(`Зміни маршруту успішно збережено! Оновлено ${result.updated_count} відправлень для водія ${selectedDriver}.`);
    } catch (error) {
      console.error("Помилка збереження змін в маршрутах:", error);
      toast.error(`Не вдалося зберегти зміни в маршрутах: ${error.message}`);
    }
  };

  // Handle deleting a point (or multiple points at the same address)
  const handleDeletePoint = async (point, additionalPoints = []) => {
    try {
      // Capture current map view state before deletion
      // We'll use this to preserve view state after reload
      const currentViewState = MapViewPreserver.viewState;

      // Create a combined array of all points to delete
      const allPoints = [point, ...additionalPoints];
      
      // Collect all shipment names for batch removal
      const shipmentNames = allPoints.map(p => p.shipment_name);
      
      // Make a single batch API call
      const result = await removeShipmentFromRoute(
        shipmentNames,
        iterationId,
        point.driver_id
      );
      
      console.log('Result:', result);
      
      if (allPoints.length > 1) {
        toast.success(`${result.removed_count || allPoints.length} зупинок успішно видалено!`);
      } else {
        toast.success(`Зупинка успішно видалена!`);
      }

      // Update local state to remove the deleted points
      // This avoids having to completely reload the routes and risk losing map position
      if (selectedDriver) {
        // Create updated driver routes by removing the deleted point(s)
        const updatedDriverRoutes = {...driverRoutes};

        // Update the points for the selected driver
        if (updatedDriverRoutes[selectedDriver]) {
          // Create a list of shipment IDs to remove
          const shipmentIdsToRemove = new Set([
            point['Delivery Name'],
            ...(additionalPoints || []).map(p => p['Delivery Name'])
          ]);

          // Filter out the deleted points from the driver's route
          updatedDriverRoutes[selectedDriver] = updatedDriverRoutes[selectedDriver].filter(
            p => !shipmentIdsToRemove.has(p['Delivery Name'])
          );

          // Update state with the filtered routes
          setDriverRoutes(updatedDriverRoutes);
        }
      }

      // Now reload the routes from the server (in the background)
      // This ensures the UI is eventually consistent with the server
      // But we don't wait for it, so the map doesn't jump
      loadOptimalRoutes();

    } catch (error) {
      console.error("Error deleting point(s):", error);
      toast.error(`Failed to delete point(s): ${error.message}`);
    }
  };

  /**
   * Handle resetting a shipment's status fields
   * @param {Object} point - The shipment point to reset
   * @param {Array} additionalPoints - Additional points to reset (for grouped shipments)
   */
  const handleResetPointStatus = async (point, additionalPoints = []) => {
    try {
        // Just reset the single point
        console.log('Resetting single point:', point);

        const result = await resetShipmentStatus(point.shipment_name, iterationId, point.driverNumber);

      // Reload the routes after reset to show updated data
      loadOptimalRoutes();

    } catch (error) {
      console.error('Error resetting point status:', error);
      toast.error(`Failed to reset point status: ${error.message}`);
    }
  };

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      <MapContainer
        center={DEFAULT_MAP_CENTER}
        zoom={7}
        style={{ width: '100%', height: '100%' }}
        key="optimal-routes-map" // Keep the same key to avoid complete recreation
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {/* Component to preserve map view state across re-renders */}
        <MapViewPreserver />

        {/* Component to handle map bounds */}
        {validPoints.length > 0 && (
          <MapBoundsFitter points={validPoints} />
        )}

        {/* Render route lines for each driver */}
        {Object.entries(driverRoutes).map(([driverName, points]) => (
          <RouteDisplay
            key={`route-${driverName}-${hasUnsavedChanges}`}
            driverPoints={points}
          />
        ))}

        {/* Render markers for each address group with potentially multiple shipments */}
        {Object.entries(groupedShipments).map(([key, group]) => {
          // Skip empty groups
          if (group.shipments.length === 0) return null;

          // For home points, just show them directly
          if (group.shipments.some(s => s.isHome)) {
            const homePoint = group.shipments.find(s => s.isHome);
            return (
          <Marker
                key={`marker-home-${key}`}
                position={[group.lat, group.lng]}
                icon={getRouteStopIcon(homePoint)}
          >
            <Popup>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                      <strong className="block text-lg mb-0">Домашня адреса водія</strong>
                      <span className="bg-black text-white text-xs font-semibold px-2.5 py-0.5 rounded">
                        Початок / Кінець
                      </span>
                    </div>
                    <div className="space-y-1">
                      <div>
                        <span className="font-medium">Адреса:</span> {homePoint['Formatted Address']}
                      </div>
                      <div>
                        <span className="font-medium">Водій:</span> {homePoint.driverName}
                      </div>
                    </div>
                  </div>
                </Popup>
              </Marker>
            );
          }

          // Count shipments in this group
          const shipmentCount = group.shipments.length;

          // Determine "primary" stop info for display in multi-stop markers
          // Sort by sequence to prioritize showing the earliest stop in the route
          const sortedShipments = [...group.shipments].sort((a, b) =>
            (a.sequence || 999) - (b.sequence || 999)
          );
          const primaryShipment = sortedShipments[0];

          return (
            <Marker
              key={`marker-group-${key}`}
              position={[group.lat, group.lng]}
              icon={getRouteStopIcon(primaryShipment, shipmentCount)}
            >
              <Popup>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <strong className="block text-lg mb-0">
                      {primaryShipment['Delivery Name'] === 'DEPO'
                        ? 'DEPO'
                        : (shipmentCount > 1
                          ? `${shipmentCount} Shipments at this Address`
                          : `Delivery ${primaryShipment['Delivery Name']}`)}
                    </strong>
                    {primaryShipment.displayOrder && (
                    <span className="bg-blue-100 text-blue-800 text-xs font-semibold px-2.5 py-0.5 rounded">
                        Stop #{primaryShipment.displayOrder}
                    </span>
                  )}
                </div>

                  <div className="space-y-1">
                    <div>
                      <span className="font-medium">Адреса:</span> {group.address}
                    </div>

                    {shipmentCount > 1 && primaryShipment['Delivery Name'] !== 'DEPO' && (
                      <div className="mt-2">
                        <span className="font-medium block mb-1">Посилки:</span>
                        <ul className="list-disc pl-5 text-sm max-h-32 overflow-y-auto">
                          {sortedShipments.map(shipment => (
                            <li key={shipment['Delivery Name']} className="mb-1">
                              <div className="flex items-center">
                                <span className="mr-1 text-xs px-1.5 py-0.5 bg-gray-100 rounded">
                                  #{shipment.displayOrder}
                                </span>
                                <span>{shipment['Delivery Name']} ({shipment['Delivery Type']})</span>
                              </div>
                            </li>
                          ))}
                        </ul>
                    </div>
                  )}

                    {shipmentCount === 1 || primaryShipment['Delivery Name'] === 'DEPO' ? (
                      <>
                    <div>
                          <span className="font-medium">Тип зупинки:</span> {primaryShipment['Delivery Type']}
                    </div>
                    <div>
                          <span className="font-medium">Ім'я:</span> {primaryShipment.Name}
                    </div>
                    <div>
                          <span className="font-medium">Телефон:</span> {primaryShipment['Phone Number']}
                    </div>
                        {primaryShipment.weight > 0 && (
                    <div>
                            <span className="font-medium">Вага:</span> {primaryShipment.weight}
                    </div>
                  )}
                        {primaryShipment.volume > 0 && (
                    <div>
                            <span className="font-medium">Об'єм:</span> {primaryShipment.volume}
                    </div>
                  )}
                      </>
                    ) : null}

                    <div>
                      <span className="font-medium">Водій:</span> {primaryShipment.driverName}
                    </div>
                    {primaryShipment.driver_status === 'Delivered' ? (
                  <div>
                        <span className="font-medium">Статус:</span> Доставлено / Забрано ✅
                  </div>
                    ) : primaryShipment.driver_status === 'Not Delivered' ? (
                      <div>
                        <span className="font-medium">Статус:</span> Не доставлено / Не забрано ❌
                      </div>
                    ) : primaryShipment.driver_status === 'Skipped' ? (
                      <div>
                        <span className="font-medium">Статус:</span> Пропущено ⏭️
                      </div>
                    ) : null}
                    {primaryShipment.driver_comment && (
                      <div>
                        <span className="font-medium">Коментар:</span> {primaryShipment.driver_comment}
                      </div>
                    )}
                    {primaryShipment.arrival_time && (
                      <div>
                        <span className="font-medium">Очікуваний час прибуття:</span> {primaryShipment.arrival_time.split('T')[1].split('.')[0]}
                      </div>
                    )}
                    {primaryShipment.driver_delivered_at && (
                      <div>
                        <span className="font-medium">Доставлено о:</span> {primaryShipment.driver_delivered_at.split('T')[1].split('.')[0]}
                      </div>
                    )}
                    <div className="mt-2">
                      <span className="font-medium">Повідомлення для водія:</span>
                      <input
                        type="text"
                        value={messageText || primaryShipment.shipment_comment}
                        defaultValue={primaryShipment.shipment_comment}
                        onChange={(e) => setMessageText(e.target.value)}
                        className="w-full mt-1 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="Фактичний номер телефону, або додаток до номеру будинку, і т.д."
                      />
                      {messageText.trim() && (
                        <button
                          onClick={async () => {
                            try {
                              const result = await addCommentToShipment(iterationId, primaryShipment['Delivery Name'], messageText);
                              toast.success('Збережено! Щоб відправити водію, натисніть Update Telegram Bot');
                              setMessageText('');
                              loadOptimalRoutes();
                            } catch (error) {
                              toast.error('Помилка при збереженні повідомлення', error);
                            } 
                          }}
                          className="mt-2 px-3 py-1 bg-indigo-600 text-white text-sm font-medium rounded hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                          Зберегти
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="mt-2">
                    {shipmentCount > 1 && primaryShipment['Delivery Name'] !== 'DEPO' ? (
                      <div className="bg-yellow-50 border border-yellow-100 p-2 rounded text-sm text-yellow-800 mb-2">
                        Кілька зупинок в цьому місці. Видалення усіх зупинок з маршруту.
                      </div>
                    ) : null}
                    <div className="flex space-x-2">
                      {/* Reset button */}
                      {primaryShipment.driver_status === 'Delivered' || primaryShipment.driver_status === 'Not Delivered' || primaryShipment.driver_status === 'Skipped' ? (
                      <button
                        onClick={() => {
                          // For multiple shipments, reset all shipments at this address
                          if (shipmentCount > 1 && primaryShipment['Delivery Name'] !== 'DEPO') {
                            // Primary shipment is already the first one, we just need to pass the rest
                            const otherShipments = sortedShipments.slice(1);
                            handleResetPointStatus(primaryShipment, otherShipments);
                          } else {
                            handleResetPointStatus(primaryShipment);
                          }
                        }}
                        className="px-3 py-1 bg-yellow-500 text-white text-sm font-medium rounded hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-400"
                      >
                        Видалити статус {primaryShipment.driver_status} щоб водій знову бачив цю зупинку {shipmentCount > 1 && primaryShipment['Delivery Name'] !== 'DEPO' ? ` Всі (${shipmentCount})` : ''}
                      </button>
                      ) : null}
                      {/* Delete button */}
                      <button
                        onClick={() => {
                          // For multiple shipments, delete all shipments at this address
                          if (shipmentCount > 1 && primaryShipment['Delivery Name'] !== 'DEPO') {
                            // Primary shipment is already the first one, we just need to pass the rest
                            const otherShipments = sortedShipments.slice(1);
                            handleDeletePoint(primaryShipment, otherShipments);
                          } else {
                            handleDeletePoint(primaryShipment);
                          }
                        }}
                        className="px-3 py-1 bg-red-600 text-white text-sm font-medium rounded hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                      >
                        Видалити {shipmentCount > 1 && primaryShipment['Delivery Name'] !== 'DEPO' ? ` Всі (${shipmentCount})` : ''}
                      </button>
                    </div>
                  </div>
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>

      {/* Routes Panel Toggle Button */}
      <div style={{ position: 'absolute', top: '10px', right: '10px', zIndex: 1000 }}>
        <button
          onClick={() => setIsRoutesPanelOpen(!isRoutesPanelOpen)}
          className="px-4 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          {isRoutesPanelOpen ? 'Сховати маршрути водіїв' : 'Показати маршрути водіїв'}
        </button>
      </div>

      {/* Routes Panel */}
      {isRoutesPanelOpen && (
        <div style={{
          position: 'absolute',
          top: '50px',
          right: '10px',
          width: '300px',
          maxHeight: 'calc(100% - 60px)',
          overflowY: 'auto',
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          padding: '16px',
          zIndex: 1000
        }}>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Маршрути водіїв</h3>

          {/* Unsaved Changes Indicator */}
          {hasUnsavedChanges && (
            <div className="mb-4 flex flex-col space-y-2">
              <div className="p-2 bg-yellow-50 border border-yellow-100 rounded text-yellow-800 text-sm flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                У вас є не збережені зміни в маршрутах
              </div>
              <button
                onClick={saveRouteChanges}
                className="w-full px-3 py-2 bg-green-600 text-white text-sm font-medium rounded hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                Зберегти зміни в маршрутах
              </button>
            </div>
          )}

          {/* Driver Selector */}
          <div className="mb-4">
            <label htmlFor="driverSelect" className="block text-sm font-medium text-gray-700 mb-1">
              Виберіть водія:
            </label>
            <select
              id="driverSelect"
              value={selectedDriver || ''}
              onChange={(e) => setSelectedDriver(e.target.value)}
              className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
            >
              {Object.keys(driverRoutes).map(driver => (
                <option key={driver} value={driver}>
                  {driver} ({driverRoutes[driver]?.length - 1 || 0} зупинки)
                </option>
              ))}
            </select>
          </div>

          {/* Points List for Selected Driver */}
          {selectedDriver && driverRoutes[selectedDriver] && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Порядок зупинок:</h4>
              <p className="text-sm text-gray-500 mb-2">Перетягніть і покладіть в потрібний порядок</p>
              <div className="border rounded-md overflow-hidden">
                <DriverRouteDndList
                  points={driverRoutes[selectedDriver]}
                  onReorder={(reorderedPoints) => {
                    // Re-sequence points after drag-and-drop
                    const newDriverRoutes = { ...driverRoutes };
                    const newValidPoints = [...validPoints];
                    let seq = 1; // start display order from 1 for real stops
                    const updatedPoints = reorderedPoints.map((point, idx) => {
                      // Home points always have sequence 0
                      const newSequence = point.isHome ? 0 : seq++;
                      const updatedPoint = { ...point, sequence: newSequence, displayOrder: newSequence.toString() };
                      // If group, update all grouped shipments
                      if (updatedPoint.isGroup && updatedPoint.groupedShipments) {
                        updatedPoint.groupedShipments = updatedPoint.groupedShipments.map(shipment => ({
                          ...shipment,
                          sequence: newSequence,
                          displayOrder: newSequence.toString(),
                        }));
                      }
                      // Update validPoints as well
                      if (updatedPoint.isGroup && updatedPoint.groupedShipments) {
                        updatedPoint.groupedShipments.forEach(groupShipment => {
                          const shipmentIdx = newValidPoints.findIndex(vp =>
                            vp['Delivery Name'] === groupShipment['Delivery Name'] &&
                            vp.driverName === selectedDriver
                          );
                          if (shipmentIdx !== -1) {
                            newValidPoints[shipmentIdx] = {
                              ...newValidPoints[shipmentIdx],
                              sequence: newSequence,
                              displayOrder: newSequence.toString(),
                            };
                          }
                        });
                      } else {
                        const pointIdx = newValidPoints.findIndex(vp =>
                          vp.id === updatedPoint.id &&
                          vp.driverName === selectedDriver
                        );
                        if (pointIdx !== -1) {
                          newValidPoints[pointIdx] = {
                            ...newValidPoints[pointIdx],
                            sequence: newSequence,
                            displayOrder: newSequence.toString(),
                          };
                        }
                      }
                      return updatedPoint;
                    });
                    newDriverRoutes[selectedDriver] = updatedPoints;
                    updateStateAfterSequenceChange(newDriverRoutes, newValidPoints, updatedPoints);
                  }}
                />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default OptimalRoutesMap;
